import type { ReactNode } from 'react'
import {
  CircleIcon, ClapIcon, EnterIcon, FileIcon, FitIcon, FrameIcon, HandIcon, ImageIcon, LogInIcon, LogOutIcon,
  PointerIcon, SparkleIcon, TypeIcon, UndoIcon, UploadIcon, ZapIcon,
} from '../ProductFrameIcons'
import { canvasBg, ToolRail } from './ToolRail'

/**
 * Workflow: the node editor as the app draws it (recorded from the 0.1.6 build):
 * an icon and an uppercase name over each node, a ··· menu, type handles on the
 * node edges, prompt nodes with upload and send, and the frame node's pickers.
 */
type Handle = { side: 'left' | 'right'; top: number; Icon: typeof ImageIcon }

function Node({ x, y, w, title, Icon, handles, children }: {
  x: number
  y: number
  w: number
  title: string
  Icon: typeof ImageIcon
  handles: Handle[]
  children: ReactNode
}) {
  return (
    <div className="absolute" style={{ left: x, top: y, width: w }}>
      <span className="mb-1 flex h-4 items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-wide text-bone">
        <Icon width={10} height={10} />
        {title}
        <span className="ml-auto tracking-normal text-faint">···</span>
      </span>
      <div className="relative rounded-lg border border-line bg-card p-2.5 shadow-[0_2px_8px_rgba(2,2,2,0.05)]">
        {children}
        {handles.map(({ side, top, Icon: H }, i) => (
          <span
            key={i}
            className={`absolute flex size-4 items-center justify-center text-muted ${side === 'left' ? '-left-[22px]' : '-right-[22px]'}`}
            style={{ top }}
          >
            <H width={10} height={10} />
          </span>
        ))}
      </div>
    </div>
  )
}

function Prompt({ placeholder, value }: { placeholder: string; value?: string }) {
  return (
    <div className="flex h-[92px] flex-col">
      <p className={`text-[10.5px] leading-snug ${value ? 'text-bone' : 'text-faint'}`}>{value ?? placeholder}</p>
      <span className="mt-auto flex items-center justify-end gap-1.5 text-muted">
        <UploadIcon width={10} height={10} />
        <span className="flex size-5 items-center justify-center rounded-md bg-panel">
          <EnterIcon width={10} height={10} />
        </span>
      </span>
    </div>
  )
}

const pick = 'mt-1 flex h-6 items-center justify-between rounded-md border border-line px-2 text-[10px] text-bone'
const tool = 'flex size-8 items-center justify-center rounded-md text-muted'

export function FlowView() {
  return (
    <div className={`absolute inset-0 ${canvasBg}`}>
      <ToolRail />
      {/* Handle centres: node x/y + 20 px header + 1 px border + handle top + 8; sides ±22 px off the card. */}
      <svg className="absolute inset-0 size-full" aria-hidden>
        <g fill="none" stroke="rgba(2,2,2,0.3)" strokeWidth="1.2">
          <path d="M338 87 C 380 87, 380 297, 422 297" />
          <path d="M690 75 C 722 75, 722 75, 754 75" />
        </g>
        <path d="M690 297 C 722 297, 722 297, 754 297" fill="none" stroke="#f2a516" strokeWidth="1.4" strokeDasharray="4 3" />
      </svg>

      <Node x={96} y={36} w={228} title="poster-04.png" Icon={FrameIcon} handles={[{ side: 'right', top: 22, Icon: ImageIcon }]}>
        <span className="text-[10px] text-muted">Artboard frame</span>
        <span className={pick}>
          poster-04.png <span className="text-faint">▾</span>
        </span>
        <span className="mt-2 block text-[10px] text-muted">Use version</span>
        <span className={pick}>
          v1 · imported <span className="text-faint">▾</span>
        </span>
        <img src="/images/medium-photography.jpg" alt="" className="mx-auto mt-2.5 h-[108px] w-[86px] object-cover" />
        <span className="mt-2.5 flex h-6 items-center justify-center rounded-md border border-line text-[10px] text-bone">
          Show in Artboard
        </span>
      </Node>

      <Node
        x={436}
        y={36}
        w={240}
        title="Text node 1"
        Icon={FileIcon}
        handles={[
          { side: 'left', top: 10, Icon: TypeIcon },
          { side: 'right', top: 10, Icon: TypeIcon },
        ]}
      >
        <Prompt placeholder="Enter your text prompt…" value="grittier, relit from the left, rough paper grain, keep the swoosh" />
      </Node>

      <Node
        x={436}
        y={258}
        w={240}
        title="Image create 1"
        Icon={SparkleIcon}
        handles={[
          { side: 'left', top: 10, Icon: ImageIcon },
          { side: 'left', top: 36, Icon: CircleIcon },
          { side: 'left', top: 62, Icon: TypeIcon },
          { side: 'right', top: 10, Icon: ImageIcon },
        ]}
      >
        <Prompt placeholder="Describe the image you want to create…" />
      </Node>

      <Node
        x={768}
        y={36}
        w={240}
        title="Video create 1"
        Icon={SparkleIcon}
        handles={[
          { side: 'left', top: 10, Icon: TypeIcon },
          { side: 'left', top: 36, Icon: ImageIcon },
          { side: 'right', top: 10, Icon: ClapIcon },
        ]}
      >
        <Prompt placeholder="Describe the video you want to create…" />
      </Node>

      <Node
        x={768}
        y={258}
        w={260}
        title="Shader node 1"
        Icon={ZapIcon}
        handles={[
          { side: 'left', top: 10, Icon: ImageIcon },
          { side: 'right', top: 10, Icon: ImageIcon },
        ]}
      >
        <div className="flex h-[96px] flex-col items-center justify-center gap-1 rounded-md bg-panel text-faint">
          <ZapIcon width={12} height={12} />
          <span className="text-[9px]">No shader yet. Pick one from the Library or write code.</span>
        </div>
        <span className="mt-2 flex gap-1.5 text-[9.5px] text-muted">
          {['Pause', 'Restart', 'Capture'].map((t) => (
            <span key={t} className="flex items-center gap-1 rounded-md border border-line px-1.5 py-0.5">
              {t === 'Restart' ? <UndoIcon width={9} height={9} /> : null}
              {t}
            </span>
          ))}
        </span>
      </Node>

      <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-xl border border-line bg-card p-1 shadow-[0_2px_8px_rgba(2,2,2,0.05)]">
        <span className={`${tool} bg-panel text-bone`}>
          <PointerIcon width={15} height={15} />
        </span>
        <span className={tool}>
          <HandIcon width={15} height={15} />
        </span>
        <span className={tool}>
          <FitIcon width={15} height={15} />
        </span>
        <span className="mx-1 h-5 w-px bg-line" />
        <span className={tool}>
          <LogInIcon width={15} height={15} />
        </span>
        <span className={tool}>
          <LogOutIcon width={15} height={15} />
        </span>
      </div>
    </div>
  )
}
